import baseGoApi from './baseGoApi';
import { User } from '../types/User';
import { IndexIncreaseResponse, UserPriceRow } from '@/types/UserPrice';
import {ChartDataApiResponse} from "@/types/Chart.tsx";
import {
    ScreeningAIStatsResponse,
    ScreeningListResponse,
    ScreeningSource,
    SetScreeningHiddenResponse,
} from "@/types/ScreeningStats";

type AdminUserUpdateDto = {
    role: string;
    status?: string;
    // Add other admin-specific fields as needed
};

type InviteUserDto = {
    email: string;
    lang: string;
    organisationId: number;
    roles: string[];
    unit: string;
    levels?: string[];
};

export type UserStatistics = {
    lastLogin: string;
    levels: string[];
    monthlyCounts: {
        appointments: number;
        month: string;
        playerTrainings: number;
        screenings: number;
    }[];
};

const adminApi = {
    // Get all users with admin details
    getAllUsers: (params: Record<string, any> = {}) => 
        baseGoApi.get<User[]>('/admin/user', { params }),

    // Get users by organisation ID
    getUsersByOrganisation: (orgId: number | string) => 
        baseGoApi.get<User[]>('/admin/user/org', { params: { orgId: orgId } }),

    // Get detailed user info (admin view)
    getUserDetails: (id: number | string) => 
        baseGoApi.get<User>(`/admin/user/${id}`),

    // Update user role and status
    updateUser: (id: number | string, data: AdminUserUpdateDto) => 
        baseGoApi.patch<User>(`/admin/user/${id}`, data, {
            headers: {
                "Content-Type": "application/merge-patch+json",
            },
        }),

    // Delete user (admin operation)
    deleteUser: (id: number | string) => 
        baseGoApi.delete<void>(`/admin/user/${id}`),

    // Invite new user
    inviteUser: (data: InviteUserDto) =>
        baseGoApi.post<User>('/admin/user/invite', data),

    // Keep the user's access levels but leave them off the organisation's invoice
    setLicenseExempt: (id: number | string, licenseExempt: boolean) =>
        baseGoApi.put<User>(`/admin/user/${id}/license-exempt`, { licenseExempt }),

    // Give one user their own price for the levels listed. Levels left out
    // follow the organisation's price list; an empty object clears them all.
    // This is what the invoice scheduler bills through Fortnox and Stripe.
    setLevelPrices: (id: number | string, levelPrices: Record<string, number>) =>
        baseGoApi.put<User>(`/admin/user/${id}/level-prices`, { levelPrices }),

    // Every billable coach with the price they are on today.
    getUserPrices: () => baseGoApi.get<UserPriceRow[]>('/admin/user-prices'),

    // Raise the selected coaches by a percentage, starting from what each of
    // them pays today. Returns the whole table again.
    applyIndexIncrease: (userIds: number[], percent: number) =>
        baseGoApi.post<IndexIncreaseResponse>('/admin/user-prices/index-increase', { userIds, percent }),

    overview: () => 
        baseGoApi.get(`/admin/overview`),

    getStatisticData: (params: {
        startDate: string;
        endDate: string;
        types?: string;   // comma-separated: "Screening,Appointments"
        orgIds?: string;  // comma-separated organisation IDs
        userIds?: string; // comma-separated user IDs
    }) => {
        return baseGoApi.get<ChartDataApiResponse>('/admin/statistic/json', { params });
    },

    // Get pricing overview for subscriptions
    getPricingOverview: () => 
        baseGoApi.get('/admin/pricing-overview'),

    // Get organisation subscription (admin)
    getOrganisationSubscription: (orgId: number | string) =>
        baseGoApi.get(`/admin/organisation/${orgId}/subscription`),

    // Get organisation invoices (admin)
    getOrganisationInvoices: (orgId: number | string) =>
        baseGoApi.get(`/admin/organisation/${orgId}/invoices`),

    // Update user email
    updateUserEmail: (id: number | string, email: string) =>
        baseGoApi.put<void>(`/admin/user/${id}/email`, { email }),

    // Get user statistics
    getUserStatistics: (id: number | string) =>
        baseGoApi.get<UserStatistics>(`/admin/user/${id}/statistics`),

    // Get user activity
    getUserActivity: (id: number | string, params: { startDate: string; endDate: string }) =>
        baseGoApi.get<UserActivity>(`/admin/user/${id}/activity`, { params }),

    // Switch current admin user's organisation
    switchCurrentUserOrganisation: (organisationId: number) =>
        baseGoApi.post(`/admin/user/org`, { organisationId }),

    // Impersonate target user. Backend returns a short-lived JWT (~1h) that
    // carries an impersonator_sub claim so all subsequent requests are logged
    // with the admin's identity as well.
    impersonateUser: (id: number | string) =>
        baseGoApi.post<ImpersonateResponse>(`/admin/user/${id}/impersonate`),

    // Aggregated Screening AI measurement statistics (rotations + body lengths).
    // Optional country limits the aggregation to the connected coach's country.
    getScreeningAIStats: (country?: string) =>
        baseGoApi.get<ScreeningAIStatsResponse>('/admin/statistic/screening-ai', {
            params: country ? { country } : undefined,
        }),

    // Aggregated legacy (manual) screening measurement statistics
    getClassicScreeningStats: (country?: string) =>
        baseGoApi.get<ScreeningAIStatsResponse>('/admin/statistic/screening-classic', {
            params: country ? { country } : undefined,
        }),

    // Every AI + classic screening as one searchable list (incl. hidden rows)
    getScreeningList: () =>
        baseGoApi.get<ScreeningListResponse>('/admin/statistic/screenings'),

    // Hide a screening with bad data from the statistics, or un-hide it
    setScreeningHidden: (source: ScreeningSource, id: number, hidden: boolean) =>
        baseGoApi.patch<SetScreeningHiddenResponse>(
            `/admin/statistic/screenings/${source}/${id}/hidden`,
            { hidden }
        ),
};

export type ImpersonateResponse = {
    jwt_token: string;
    expires_at: string;
    admin_email: string;
    target_user: {
        id: number;
        email: string;
        name?: string;
    };
};

export type UserActivity = {
    userId: number;
    startDate: string;
    endDate: string;
    dailyData: {
        date: string;
        requestCount: number;
    }[];
};

export default adminApi; 